import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
  Switch,
  Tabs,
  Tab,
  Button,
  Chip
} from '@mui/material';
import { BsEyeFill } from 'react-icons/bs';
import { useNavigate } from 'react-router';
import MainCard from 'components/MainCard';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Lottie from 'react-lottie';
import lottieLoader from '../../assets/Lottie/NoLoaderData.json';
import { getCoach } from 'services/coach/getCoach';
import { updateCoachStatus } from 'services/coach/updateCoachStatus';
import toast from 'react-hot-toast';
import { RestOutlined } from '@ant-design/icons';
import { MdOutlineLockReset, MdOutlinePassword, MdOutlineRestartAlt, MdRestartAlt } from 'react-icons/md';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import ResetPasswordDialog from 'components/coach/ResetPasswordDialog';
import { resetCoachPassword } from 'services/coach/resetCoachPassword';

const loaderOptions = {
  loop: true,
  autoplay: true,
  animationData: lottieLoader,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice'
  }
};


export default function CoachesList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [coaches, setCoaches] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const { data, isLoading, error } = useQuery({
    queryFn: () => getCoach(),
    queryKey: ['coaches']
  });

  const coachData = data || [];
  console.log('coachData', coachData);

  useEffect(() => {
    if (coachData?.length) {
      const enriched = coachData
        .filter((coach) => coach.coachid)
        .map((coach) => ({
          coachId: coach.coachid,
          coachName: coach.name || coach.username || 'N/A',
          coachEmail: coach.email || 'N/A',
          coachContact: coach.contact || 'N/A',
          registrationDate: coach.created ? new Date(coach.created).toLocaleDateString() : 'N/A',
          studentsCount: coach.studentsCount,
          coachStatus: coach.status || 2
        }));
      console.log('Processed coaches:', enriched);
      setCoaches(enriched);
    }
  }, [coachData]);

  const updateCoachMutation = useMutation({
    mutationFn: updateCoachStatus,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['coaches']);
      toast.success(data);
    },
    onError: (error) => {
      console.error('Error updating status:', error);
      toast.error(error?.response?.data?.message || 'Failed to update coach status');
    }
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleToggleStatus = (coach) => {
    const newStatus = coach.coachStatus === 1 ? 2 : 1;

    // Update backend
    updateCoachMutation.mutate({
      coachid: coach.coachId,
      key: newStatus
    });
  };

  const resetPasswordMutation = useMutation({
    mutationFn: resetCoachPassword,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['coaches']);
      toast.success(data);
    },
    onError: (error) => {
      console.error('Error Resetting coach password:', error);
      toast.error(error?.response?.data?.message || 'Failed to reset coach password');
    }
  });

  const handleResetPassword = (coach) => {
    setSelectedCoach(coach);
    setOpenDialog(true);
  };

  const confirmResetPassword = () => {
    if (!newPassword || !confirmPassword) {
      setPasswordError('Both fields are required.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    console.log(`Resetting password for ${selectedCoach.coachName}: ${newPassword}`);

    resetPasswordMutation.mutate({
      coachid: selectedCoach.coachId,
      password: newPassword
    });

    // Reset fields and close dialog
    setOpenDialog(false);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const allCount = coaches.length;
  const activeCount = coaches.filter((c) => c.coachStatus === 1).length;
  const inactiveCount = coaches.filter((c) => c.coachStatus === 2).length;

  const filteredByTab = coaches.filter((coach) => {
    if (tabValue === 1) return coach.coachStatus === 1; // Active
    if (tabValue === 2) return coach.coachStatus === 2; // Inactive
    return true; // All
  });

  const filteredCoaches = filteredByTab.filter((coach) => coach.coachName.toLowerCase().includes(searchTerm.toLowerCase()));

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <MainCard>
        <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>Error fetching coach data: {error.message}</Box>
      </MainCard>
    );
  }

  return (
    <MainCard>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All" icon={<Chip label={allCount} color="primary" size="small" />} iconPosition="end" />
          <Tab label="Active" icon={<Chip label={activeCount} color="success" size="small" />} iconPosition="end" />
          <Tab label="Inactive" icon={<Chip label={inactiveCount} color="error" size="small" />} iconPosition="end" />
        </Tabs>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Search Coach" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          {/* <Button variant="contained" color="primary" onClick={() => navigate('/settings/coaches/create')}>
            Create Coach
          </Button> */}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 800 }} aria-label="coach table">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Coach Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Joined Date</TableCell>
              <TableCell>Students</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCoaches.length > 0 ? (
              filteredCoaches.map((coach, index) => (
                <TableRow key={coach.coachId}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    {coach.coachName
                      .toLowerCase()
                      .split(' ')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')}
                  </TableCell>
                  <TableCell>{coach.coachEmail}</TableCell>
                  <TableCell>{coach.coachContact}</TableCell>
                  <TableCell>{coach.registrationDate}</TableCell>
                  <TableCell>{coach?.studentsCount}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Chip label={coach.coachStatus === 1 ? 'Active' : 'Inactive'} color={coach.coachStatus === 1 ? 'success' : 'error'} />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Tooltip title="Reset Password">
                        <IconButton onClick={() => handleResetPassword(coach)}>
                          <MdOutlineLockReset />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View">
                        <IconButton color="secondary" onClick={() => navigate('/coaches-List/coach-details', { state: { coach } })}>
                          <BsEyeFill />
                        </IconButton>
                      </Tooltip>
                      <Switch checked={coach.coachStatus === 1} onChange={() => handleToggleStatus(coach)} color="primary" />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Lottie options={loaderOptions} height="25%" width="25%" />
                  <Box sx={{ mt: 2 }}>No coaches found matching your criteria.</Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ResetPasswordDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        coach={selectedCoach}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
        onNewPasswordChange={(e) => setNewPassword(e.target.value)}
        onConfirmPasswordChange={(e) => setConfirmPassword(e.target.value)}
        onSubmit={confirmResetPassword}
        error={passwordError}
      />
    </MainCard>
  );
}
